import * as routes from '@/pages/routes.ts';
import { DollarOutlined, LoginOutlined, UnlockOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { Modal, Switch } from 'antd';
import { AgencyBean, ListResponseAgencyBean, LogBean } from 'common/api/api';
import { financialBlockApi } from 'common/api/FinancialBlockApi';
import { accountApi, agencyApi } from 'common/api/entitiesApi';
import { ActiveStatusSelector } from 'common/components/dictionary/ActiveStatusSelector.tsx';
import { ListActionButton } from 'common/components/ListActionButton.tsx';
import { ListCard } from 'common/components/ListCard';
import { SearchInput } from 'common/components/SearchInput';
import { ListFilters, useAppContext } from 'common/context/AppContext';
import { COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH } from 'common/util/constants';
import { initListFilters, useListFilters } from 'common/util/useListFilters';
import { apiCall, formatDollars, showError, withActionsMediaQuery } from 'common/util/Util';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';





const AgencyList = () => {
    const appContext = useAppContext()
    const [filters, setFilters] = useState<ListFilters>(initListFilters())
    const {setFilter} = useListFilters(setFilters)
    const [loading, setLoading] = useState<boolean>(false)
    const [listData, setListData] = useState<ListResponseAgencyBean>()
    const navigateTo = useNavigate()

    function loginAsAgency(id: number) {
        accountApi
            .loginAsAgency(id)
            .then((response) => {
                window.open(response.data, '_blank')
            })
            .catch((e) => showError(appContext, e))
    }

    const loadData = () => {
        apiCall(
            setLoading,
            appContext,
            () =>
                agencyApi.agencyPage(filters.sort?.name, filters.sort?.direction, filters.name, filters.active, {
                    query: {page: filters.page, size: filters.pageSize},
                }),
            (data) => setListData(data),
        )
    }

    const toggleActive = (record: AgencyBean, checked: boolean) => {
        if (record.id) {
            const id = record.id
            apiCall(
                setLoading,
                appContext,
                () => (checked ? agencyApi.agencyActivate(id) : agencyApi.agencyDeactivate(id)),
                () => {
                    record.active = checked
                    setListData(listData)
                },
            )
        }
    }

    const toggleUiAccess = (record: AgencyBean, checked: boolean) => {
        if (record.id) {
            const id = record.id
            apiCall(
                setLoading,
                appContext,
                () => (checked ? agencyApi.allowUiAccess(id) : agencyApi.restrictUiAccess(id)),
                () => {
                    record.uiAccess = checked
                    setListData(listData)
                },
            )
        }
    }

    const unblockPayments = (record: AgencyBean) => {
        if (!record.id) return
        Modal.confirm({
            title: `Unblock payments for ${record.name}?`,
            content: 'Do this only after the refund or dispute has been reviewed.',
            okText: 'Unblock',
            onOk: () => financialBlockApi.unblockAgency(record.id as number)
                .then(loadData)
                .catch((error) => showError(appContext, error)),
        })
    }

    return (
        <ListCard
            loading={loading}
            title="Agencies"
            createName="Agency"
            createRoute={routes.AGENCY_CREATE}
            filters={filters}
            setFilters={setFilters}
            listData={listData}
            loadData={loadData}
            columns={[
                {
                    title: 'ID',
                    dataIndex: 'id',
                    width: COLUMN_ID_WIDTH,
                    fixed: 'left',
                },
                {
                    title: 'Name',
                    dataIndex: 'name',
                    className: 'list-table-name',
                    sorter: true,
                    ellipsis: true,
                    render: (text, record) => (
                        <Link style={{color: '#5888CF', minWidth: 100}} to={`${routes.AGENCY}/${record.id}/edit`}>
                            {text}
                        </Link>
                    ),
                    width: COLUMN_NAME_WIDTH,
                    fixed: 'left',
                },
                {
                    title: 'Public Id',
                    dataIndex: 'publicId',
                    width: 80
                },
                {
                    title: 'Accounts',
                    dataIndex: 'accounts',
                    render: (value, record) => (
                        <Link to={`/account?agencyId=${record.id}`} style={{color: '#5888CF'}}>
                            {value}
                        </Link>
                    ),
                    width: 80,
                },
                {
                    title: 'System SSP Budget',
                    render: (bean: AgencyBean) => formatDollars(bean.systemSspBudgetAvailable as number),
                    width: 100,
                },
                {
                    title: 'Total Paid',
                    render: (bean: AgencyBean) => formatDollars(bean.systemSspTotalBudget as number),
                    width: 100,
                },
                {
                    title: 'Last Modified',
                    dataIndex: 'modified',
                    sorter: true,
                    width: 100,
                    render: (date) => date && dayjs(date).format('DD MMM YY'),
                },
                {
                    title: 'UI Access',
                    dataIndex: 'uiAccess',
                    render: (val, record: AgencyBean) => (
                        <Switch
                            size="small"
                            checked={record.uiAccess}
                            onChange={(checked) => record.id && toggleUiAccess(record, checked)}
                        />
                    ),
                    width: 60,
                },
                {
                    title: 'Active',
                    dataIndex: 'active',
                    sorter: true,
                    render: (val, record: AgencyBean) => (
                        <Switch
                            size="small"
                            checked={record.active}
                            onChange={(checked) => record.id && toggleActive(record, checked)}
                        />
                    ),
                    width: 60,
                },
                {
                    title: 'Actions',
                    key: 'action',
                    width: 120,
                    render: (_, record: AgencyBean) => (
                        <div style={{display: 'flex', gap: 8, justifyContent: 'center'}}>
                            <ListActionButton onClick={() => loginAsAgency(record.id as number)} tooltip="Login As">
                                <LoginOutlined/>
                            </ListActionButton>
                            <ListActionButton onClick={() => navigateTo(`/logs/${LogBean.EntityEnum.Agency}/${record.id}`)}
                                    tooltip="Logs">
                                <UnorderedListOutlined/>
                            </ListActionButton>
                            <ListActionButton onClick={() => navigateTo(`/agency-transactions/create/${record.id}`)} tooltip="Add Transaction">
                                <DollarOutlined />
                            </ListActionButton>
                            {record.paymentBlocked && (
                                <ListActionButton danger onClick={() => unblockPayments(record)}
                                        tooltip={`Unblock payments${record.paymentBlockedReason ? `: ${record.paymentBlockedReason}` : ''}`}>
                                    <UnlockOutlined />
                                </ListActionButton>
                            )}
                        </div>
                    ),
                },
            ]}
        >
            <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)}/>
            <ActiveStatusSelector
                placeholder="Filter by Active"
                style={{minWidth: '100px'}}
                value={filters.active}
                onChange={(value) => setFilter('active', value)}
            />
        </ListCard>
    )
}

export default withActionsMediaQuery('700px')(AgencyList)
